<template>
  <section
    class="distribution-root"
    aria-labelledby="job-categories-chart-title"
  >
    <header class="chart-header">
      <div class="chart-header-text">
        <h2 id="job-categories-chart-title" class="chart-title">
          <i class="fas fa-tags chart-title-icon" aria-hidden="true"></i>
          Popular job categories
        </h2>
        <p class="chart-subtitle">
          See which tags have the most job posts scraped in the selected
          period — useful for spotting demand (e.g. VA, IT support, video
          editing).
        </p>
        <p class="chart-period-hint">{{ periodHint }}</p>
      </div>
      <div
        class="period-toggle"
        role="group"
        :aria-label="'Time range: ' + periodHint"
      >
        <button
          v-for="opt in periodOptions"
          :key="opt.value"
          type="button"
          class="period-btn"
          :class="{ 'period-btn--active': period === opt.value }"
          :disabled="loading"
          :aria-pressed="period === opt.value"
          @click="$emit('update:period', opt.value)"
        >
          {{ opt.label }}
        </button>
      </div>
    </header>

    <div v-if="loading" class="distribution-loading" aria-live="polite">
      <div class="distribution-loading-spinner" aria-hidden="true"></div>
      <span>Updating categories…</span>
    </div>

    <div v-else-if="hasTagData" class="summary-row" role="status">
      <div class="summary-pill">
        <span class="summary-value">{{ totalPosts.toLocaleString() }}</span>
        <span class="summary-label">job posts in view</span>
      </div>
      <div class="summary-pill">
        <span class="summary-value">{{ categoryCount }}</span>
        <span class="summary-label">{{
          categoryCount === 1 ? "category" : "categories"
        }}</span>
      </div>
      <div
        v-if="topCategory"
        class="summary-pill summary-pill--highlight summary-pill--clickable"
        role="button"
        tabindex="0"
        title="View all jobs with this tag"
        @click="goToJobsByTagIndex(0)"
        @keydown.enter.prevent="goToJobsByTagIndex(0)"
        @keydown.space.prevent="goToJobsByTagIndex(0)"
      >
        <span class="summary-label">Top category</span>
        <span class="summary-top-name" :title="topCategory.name">{{
          truncate(topCategory.name, 36)
        }}</span>
        <span class="summary-value summary-value--inline">{{
          topCategory.count.toLocaleString()
        }}</span>
        <span class="summary-label">posts</span>
      </div>
    </div>

    <div v-else-if="!hasTagData" class="empty-state">
      <div class="empty-icon-wrap" aria-hidden="true">
        <i class="fas fa-chart-bar"></i>
      </div>
      <h3 class="empty-title">No tagged jobs to show yet</h3>
      <p class="empty-lead">
        This chart fills in automatically when job posts have
        <strong>tags</strong> (skills or role types).
      </p>
      <ul class="empty-tips">
        <li>Tags often come from your scrapers or when jobs are imported.</li>
        <li>Each post can have multiple tags — counts are per tag.</li>
        <li>Up to <strong>25</strong> top tags are shown, sorted by volume.</li>
      </ul>
    </div>

    <div
      v-show="hasTagData && !loading"
      class="canvas-wrap"
      :style="canvasWrapStyle"
    >
      <canvas ref="tagsCanvas" aria-label="Bar chart of job posts per tag"></canvas>
    </div>

    <p v-if="hasTagData && !loading" class="chart-footnote">
      <i class="fas fa-info-circle" aria-hidden="true"></i>
      Hover a bar for the full tag name and exact count. Categories are ordered
      from most to least job posts.
    </p>
  </section>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from "vue";
import { useRouter } from "vue-router";
import Chart from "chart.js/auto";
import { useTheme } from "@/composables/useTheme";

const MAX_LABEL_CHARS = 34;

// Resolves a themed HSL custom property (e.g. "--foreground") into a real
// color string Chart.js/canvas can render, honoring light/dark mode.
function themeColor(varName, alpha) {
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim();
  if (!value) return alpha != null ? `hsla(0, 0%, 50%, ${alpha})` : "#64748b";
  return alpha != null ? `hsl(${value} / ${alpha})` : `hsl(${value})`;
}

function truncate(str, max = MAX_LABEL_CHARS) {
  const s = String(str || "");
  if (s.length <= max) return s;
  return `${s.slice(0, Math.max(0, max - 1))}…`;
}

function barColors(count) {
  const n = Math.max(count, 1);
  return Array.from({ length: count }, (_, i) => {
    const t = i / Math.max(n - 1, 1);
    const alpha = 0.92 - t * 0.38;
    return `rgba(74, 107, 253, ${alpha.toFixed(2)})`;
  });
}

export default {
  name: "JobPostsDistributionChart",
  props: {
    chartData: {
      type: Object,
      required: true,
    },
    period: {
      type: String,
      default: "month",
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:period"],
  setup(props) {
    const router = useRouter();
    const tagsCanvas = ref(null);
    const { theme } = useTheme();
    let tagsChart = null;

    const periodOptions = [
      { value: "day", label: "Day" },
      { value: "week", label: "Week" },
      { value: "month", label: "Month" },
    ];

    const periodHint = computed(() => {
      const p = (props.period || "month").toLowerCase();
      if (p === "day") {
        return "Today (from midnight, local time) to now — by scrape date.";
      }
      if (p === "week") {
        return "This week (Mon 00:00, local time) to now — by scrape date.";
      }
      return "This calendar month to now — by scrape date.";
    });

    const topTags = computed(() => props.chartData?.top_tags || {});

    const labelsRaw = computed(() => {
      const L = topTags.value.labels || [];
      return L.map((x) => String(x ?? ""));
    });

    const countsRaw = computed(() => {
      const c = topTags.value.counts || [];
      return c.map((x) => Number(x) || 0);
    });

    const idsRaw = computed(() => {
      const ids = topTags.value.ids || [];
      return ids.map((x) => {
        const n = Number(x);
        return Number.isFinite(n) && n > 0 ? n : null;
      });
    });

    const hasTagData = computed(() => labelsRaw.value.length > 0);

    const totalPosts = computed(() =>
      countsRaw.value.reduce((a, b) => a + b, 0),
    );

    const categoryCount = computed(() => labelsRaw.value.length);

    const topCategory = computed(() => {
      if (!hasTagData.value) return null;
      return {
        name: labelsRaw.value[0],
        count: countsRaw.value[0],
      };
    });

    const canvasWrapStyle = computed(() => {
      const n = labelsRaw.value.length;
      if (n <= 0) return {};
      const h = Math.min(720, Math.max(320, 48 + n * 40));
      return { minHeight: `${h}px` };
    });

    /** Opens public job list filtered by this tag (same as Advanced Filters). */
    const goToJobsByTagIndex = (index) => {
      const labels = labelsRaw.value;
      const ids = idsRaw.value;
      if (index < 0 || index >= labels.length) return;
      const id = ids[index];
      const tagsQuery =
        id != null
          ? String(id)
          : `name:${encodeURIComponent(labels[index] || "")}`;
      router.push({ path: "/jobs", query: { tags: tagsQuery } });
    };

    const destroyChart = () => {
      if (tagsChart) {
        tagsChart.destroy();
        tagsChart = null;
      }
    };

    const buildTagsChart = () => {
      if (!tagsCanvas.value) return;

      const fullLabels = labelsRaw.value;
      const counts = countsRaw.value;
      const displayLabels = fullLabels.map((s) => truncate(s, MAX_LABEL_CHARS));

      if (tagsChart) {
        tagsChart.destroy();
        tagsChart = null;
      }

      if (fullLabels.length === 0) return;

      const ctx = tagsCanvas.value.getContext("2d");
      const mutedForeground = themeColor("--muted-foreground");
      const gridColor = themeColor("--border", 0.5);
      tagsChart = new Chart(ctx, {
        type: "bar",
        data: {
          labels: displayLabels,
          datasets: [
            {
              label: "Job posts",
              data: counts,
              backgroundColor: barColors(fullLabels.length),
              borderColor: "rgba(74, 107, 253, 0.35)",
              borderWidth: 1,
              borderRadius: 8,
              borderSkipped: false,
              barThickness: "flex",
              maxBarThickness: 36,
            },
          ],
        },
        options: {
          indexAxis: "y",
          responsive: true,
          maintainAspectRatio: false,
          layout: {
            padding: { top: 8, right: 20, bottom: 8, left: 4 },
          },
          plugins: {
            title: {
              display: false,
            },
            legend: { display: false },
            tooltip: {
              backgroundColor: "rgba(15, 23, 42, 0.94)",
              titleMarginBottom: 8,
              padding: 12,
              cornerRadius: 8,
              titleFont: { size: 14, weight: "600" },
              bodyFont: { size: 13 },
              callbacks: {
                title(items) {
                  const i = items[0]?.dataIndex ?? 0;
                  return fullLabels[i] || "";
                },
                label(ctx) {
                  const v = ctx.parsed?.x;
                  const n =
                    typeof v === "number" ? v.toLocaleString() : String(v);
                  const total = totalPosts.value;
                  const pct =
                    total > 0
                      ? Math.min(100, Math.round((Number(v) / total) * 100))
                      : 0;
                  return `${n} job posts (${pct}% of tags shown)`;
                },
              },
            },
          },
          scales: {
            x: {
              beginAtZero: true,
              grace: "5%",
              ticks: {
                precision: 0,
                color: mutedForeground,
                font: { size: 12 },
              },
              title: {
                display: true,
                text: "Number of job posts",
                color: mutedForeground,
                font: { size: 12, weight: "600" },
              },
              grid: {
                color: gridColor,
                drawBorder: false,
              },
            },
            y: {
              ticks: {
                autoSkip: false,
                color: mutedForeground,
                font: { size: 12 },
              },
              grid: { display: false, drawBorder: false },
            },
          },
          onHover(event, elements) {
            const canvas = event?.native?.target;
            if (canvas) {
              canvas.style.cursor =
                elements && elements.length ? "pointer" : "default";
            }
          },
          onClick(_event, elements) {
            if (!elements?.length) return;
            const el = elements[0];
            const idx =
              typeof el.index === "number"
                ? el.index
                : typeof el.dataIndex === "number"
                  ? el.dataIndex
                  : -1;
            if (idx >= 0) goToJobsByTagIndex(idx);
          },
        },
      });
    };

    const renderChart = async () => {
      await nextTick();
      buildTagsChart();
    };

    onMounted(() => {
      renderChart();
    });

    onUnmounted(() => {
      destroyChart();
    });

    watch(
      () => props.chartData,
      () => {
        renderChart();
      },
      { deep: true },
    );

    watch(
      () => props.loading,
      (v) => {
        if (!v) renderChart();
      },
    );

    // Rebuild the chart when the theme toggles so canvas text/grid colors
    // (which can't respond to CSS custom properties on their own) stay legible.
    watch(theme, () => {
      renderChart();
    });

    return {
      tagsCanvas,
      hasTagData,
      totalPosts,
      categoryCount,
      topCategory,
      canvasWrapStyle,
      truncate,
      goToJobsByTagIndex,
      periodOptions,
      periodHint,
    };
  },
};
</script>

<style scoped>
.distribution-root {
  width: 100%;
  display: flex;
  flex-direction: column;
  background: hsl(var(--card));
  color: hsl(var(--foreground));
  border-radius: 12px;
  padding: 1.35rem 1.5rem 1.25rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  border: 1px solid hsl(var(--border));
}

.chart-header {
  margin-bottom: 1rem;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.chart-header-text {
  flex: 1;
  min-width: 0;
}

.chart-period-hint {
  margin: 0.35rem 0 0;
  font-size: 0.8rem;
  line-height: 1.45;
  color: hsl(var(--muted-foreground));
  max-width: 40rem;
}

.period-toggle {
  display: flex;
  flex-shrink: 0;
  border-radius: 10px;
  padding: 3px;
  background: hsl(var(--muted));
  border: 1px solid hsl(var(--border));
  gap: 2px;
}

.period-btn {
  border: none;
  background: transparent;
  color: hsl(var(--muted-foreground));
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.45rem 0.75rem;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
  white-space: nowrap;
}

.period-btn:hover:not(:disabled) {
  color: hsl(var(--foreground));
  background: hsl(var(--background) / 0.8);
}

.period-btn--active {
  background: hsl(var(--background));
  color: hsl(var(--primary));
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.12);
}

.period-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.distribution-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  padding: 2rem 1rem;
  color: hsl(var(--muted-foreground));
  font-size: 0.9rem;
}

.distribution-loading-spinner {
  width: 1.25rem;
  height: 1.25rem;
  border: 2px solid hsl(var(--border));
  border-top-color: hsl(var(--primary));
  border-radius: 50%;
  animation: dist-spin 0.7s linear infinite;
}

@keyframes dist-spin {
  to {
    transform: rotate(360deg);
  }
}

.chart-title {
  margin: 0 0 0.35rem;
  font-size: 1.2rem;
  font-weight: 700;
  color: hsl(var(--foreground));
  display: flex;
  align-items: center;
  gap: 0.5rem;
  line-height: 1.3;
}

.chart-title-icon {
  color: hsl(var(--primary));
  font-size: 1.1rem;
}

.chart-subtitle {
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.5;
  color: hsl(var(--muted-foreground));
  max-width: 52rem;
}

.summary-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  margin-bottom: 1rem;
}

.summary-pill {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.35rem 0.5rem;
  padding: 0.5rem 0.85rem;
  background: hsl(var(--muted));
  border-radius: 10px;
  border: 1px solid hsl(var(--border));
}

.summary-pill--highlight {
  background: linear-gradient(135deg, hsl(var(--primary) / 0.16) 0%, hsl(var(--primary) / 0.08) 100%);
  border-color: hsl(var(--primary) / 0.25);
}

.summary-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: hsl(var(--foreground));
}

.summary-value--inline {
  font-size: 1.1rem;
}

.summary-label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: hsl(var(--muted-foreground));
}

.summary-pill .summary-label:last-of-type:not(:only-child) {
  text-transform: none;
  letter-spacing: normal;
  font-weight: 500;
  font-size: 0.8rem;
}

.summary-top-name {
  font-weight: 600;
  color: hsl(var(--primary));
  max-width: 14rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.empty-state {
  text-align: center;
  padding: 2rem 1rem 2.25rem;
  max-width: 28rem;
  margin: 0 auto;
}

.empty-icon-wrap {
  width: 4rem;
  height: 4rem;
  margin: 0 auto 1rem;
  border-radius: 50%;
  background: hsl(var(--muted));
  display: flex;
  align-items: center;
  justify-content: center;
  color: hsl(var(--muted-foreground));
  font-size: 1.75rem;
}

.empty-title {
  margin: 0 0 0.5rem;
  font-size: 1.1rem;
  font-weight: 700;
  color: hsl(var(--foreground));
}

.empty-lead {
  margin: 0 0 1rem;
  font-size: 0.9rem;
  line-height: 1.55;
  color: hsl(var(--muted-foreground));
}

.empty-tips {
  margin: 0;
  padding-left: 1.25rem;
  text-align: left;
  font-size: 0.85rem;
  line-height: 1.65;
  color: hsl(var(--muted-foreground));
}

.empty-tips li {
  margin-bottom: 0.35rem;
}

.canvas-wrap {
  flex: 1;
  min-height: 320px;
  position: relative;
}

.chart-footnote {
  margin: 0.85rem 0 0;
  font-size: 0.8rem;
  line-height: 1.45;
  color: hsl(var(--muted-foreground));
  display: flex;
  align-items: flex-start;
  gap: 0.4rem;
}

.chart-footnote .fa-info-circle {
  margin-top: 0.15rem;
  flex-shrink: 0;
}

.summary-pill--clickable {
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.summary-pill--clickable:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(74, 107, 253, 0.2);
}

.summary-pill--clickable:focus {
  outline: 2px solid #4a6bfd;
  outline-offset: 2px;
}

.canvas-wrap--interactive {
  cursor: default;
}

@media (max-width: 640px) {
  .distribution-root {
    padding: 1.1rem 1rem 1rem;
  }

  .chart-header {
    flex-direction: column;
    align-items: stretch;
  }

  .period-toggle {
    width: 100%;
    justify-content: stretch;
  }

  .period-btn {
    flex: 1;
    text-align: center;
  }

  .chart-title {
    font-size: 1.05rem;
  }

  .summary-row {
    flex-direction: column;
  }

  .summary-pill {
    width: 100%;
  }
}
</style>
